import type { CountryCode } from "@freightsimple/generated-dashboard-openapi-client/models/CountryCode";
import {
  getCountriesOfHoliday,
  getHolidaysByDate,
  HOLIDAY_CANADA_DESCRIPTION,
  HOLIDAY_US_DESCRIPTION,
  type Holiday,
} from "@freightsimple/shared/holidays/holidays";
import { getLocalDateComponents } from "@freightsimple/time/helpers";
import type { LocalDate } from "@freightsimple/time/branded-types";

/** Canada and the United States — the only countries we ship in. */
const EVERY_COUNTRY_WE_SHIP_IN = 2;

/**
 * The holidays that fall on `date` and are worth warning a customer about,
 * given what we currently know about where the shipment is being collected.
 *
 * A pickup postal code is entered part-way through booking, so for most of the
 * flow there is no country yet. Rather than show nothing until then, holidays
 * observed on both sides of the border are shown straight away. That set is
 * per-date, not per-holiday: New Year's Day and Christmas Day qualify in most
 * years, but when the nominal date is a Saturday the two countries observe them
 * on different days — the US on the Friday, Canada on the Monday — so neither
 * date is observed on both sides and neither shows until the pickup country is
 * known. Good Friday and Labour Day always qualify. Regional ones wait for the
 * pickup country either way, because Canada Day is not a holiday in Arizona and
 * showing it to a US shipper is noise.
 *
 * Delivery location never enters into this: the holiday that stops a pickup is
 * the one where the freight is collected.
 */
export function pickupHolidaysOn(
  date: LocalDate,
  pickupCountry: CountryCode | undefined,
): Holiday[] {
  const { year, month, day } = getLocalDateComponents(date);
  const onThisDay = getHolidaysByDate(year, month, day);
  if (onThisDay.length === 0) {
    return [];
  }

  if (pickupCountry === undefined) {
    return onThisDay.filter(
      (holiday) =>
        getCountriesOfHoliday(holiday).length === EVERY_COUNTRY_WE_SHIP_IN,
    );
  }

  // Ask each matched holiday which countries observe it, rather than matching
  // names against that country's calendar. The two are not the same once a
  // holiday is observed on different dates either side of the border: Canada
  // keeps Christmas 2027 on Mon Dec 27, and a name-based match would show that
  // to a US shipper, for whom Dec 27 is an ordinary working day.
  const observedHere =
    pickupCountry === "Canada"
      ? HOLIDAY_CANADA_DESCRIPTION
      : HOLIDAY_US_DESCRIPTION;
  return onThisDay.filter((holiday) =>
    getCountriesOfHoliday(holiday).includes(observedHere),
  );
}
